/**
 * Gather-tool presentation (E2.2.S3) — which tool the LOCAL player's gather beat draws in the
 * mainhand, self-view only.
 *
 * The beat is keyed off the gathering SKILL, which the wire already carries: a successful
 * `interactIntent` gather returns a private `gatherResult` whose `gatheringSkill` lands on the
 * client store's `lastGather` slice. So the whole moment is store-driven with ZERO protocol
 * change and ZERO React coupling (the caller — Game — subscribes to the store and feeds the skill
 * in). `AnimState = idle|move|dodge` carries no gather state, so OTHER players' gathering is not
 * client-knowable — this is self-view only (EQP-025 §4).
 *
 * The tool is DRAWN by the equipment renderer (`EquipmentRenderer.setHeldTool`), never by a prop
 * of its own: it takes the mainhand for the beat, seated on the hand bone with its held-grip row
 * and closing its fist exactly like an equipped tool, and a mainhand that already holds that tool
 * (the server's auto-swap, AD-844) is left alone. q-gather-tool-one-in-hand (owner phone playtest
 * 2026-10-24: "the sickle appears to duplicate and float during the animation"): the beat used
 * to be a separate `GatherToolProp` added to the avatar group at the capsule-era mainhand grip,
 * so with the auto-swapped sickle already in the hand there were two, one floating above-left of
 * the figure. One hand, one mesh: the renderer owns the slot.
 *
 * Node-safe: data and a pure resolver; import boundary (AD-122): shared types via
 * `@great-cloud/shared/client` only.
 */

import type { AssetPackDef, GatheringSkill } from '@great-cloud/shared/client';

/** The gather-tool base asset pack (EQP-025). */
export const GATHER_TOOL_PACK = 'gather-tool-bases-v1';

/**
 * `gatheringSkill` → gather-tool base id. **frontend-LOCAL by design — NOT a
 * `shared/` constant**: this is a client presentation convention, the exact
 * AD-141 / DR-020 `RESOURCE_NODE_ASSET` pattern (which likewise maps a
 * `GatheringSkill` to a placeholder asset id), kept in `frontend/`. Promoting it
 * into `shared/` would be a T3 DR — deliberately not taken in this feature
 * (recorded for the E2.2.S5 reviewer).
 *
 * Unlike `RESOURCE_NODE_ASSET`, `hunting` IS present: a hunter carries a skinning
 * knife even though hunting yields drop from enemies rather than world nodes
 * (EQP-025 §1). All five `GATHERING_SKILLS` map to one of the five committed
 * `gather-tool-bases-v1` bases.
 */
export const GATHER_TOOL_BY_SKILL: Readonly<Partial<Record<GatheringSkill, string>>> = {
  forestry: 'forestry-axe-base',
  mining: 'mining-pickaxe-base',
  herbalism: 'herbalist-sickle-base',
  hunting: 'skinning-knife-base',
  salvaging: 'salvage-pry-base',
};

/** A resolved gather-tool attachment: the pack + model to instantiate at mainhand. */
export interface GatherToolResolution {
  assetPack: string;
  model: string;
}

/**
 * Resolve a gathering skill to its tool pack + model, or `null` for an unknown /
 * absent skill (the caller then attaches nothing — the graceful path). Pure; the
 * model's pack-membership is proven by the test suite + the loader's null path
 * (this map is frontend-local, not content — `validate.ts` (AD-413) checks only
 * `presentation.model` on definitions).
 */
export function resolveGatherTool(skill: string | null | undefined): GatherToolResolution | null {
  if (skill == null || skill === '') return null;
  const model = (GATHER_TOOL_BY_SKILL as Readonly<Record<string, string | undefined>>)[skill];
  if (model === undefined) return null;
  return { assetPack: GATHER_TOOL_PACK, model };
}

/**
 * Pack lookup — the client-pack `asset-packs[id]` projection. Structurally
 * satisfied by the E2.2.S2 `EquipmentContent` (App passes the same object), so no
 * new App plumbing is needed; kept as its own minimal type to stay decoupled +
 * node-testable.
 */
export interface GatherToolContent {
  /** Client-pack `asset-packs[id]` (its `assets[]` manifest), or undefined if not delivered. */
  assetPack(id: string): AssetPackDef | undefined;
}

/** The slice of the wire `GatherResultPayload` the tool moment reads. */
export interface GatherMoment {
  /** Success flag — a failed gather (out of range / depleted) shows no tool. */
  ok: boolean;
  /** The gathering skill the resource trains (present on success only). */
  gatheringSkill?: string;
}

/**
 * The latest local gather result + a change signal. App builds this from the
 * store's `lastGather` slice (`() => store.state.lastGather` + `store.subscribe`);
 * the caller detects a NEW gather by object identity (the store replaces the slice
 * per applied `gatherResult`), so a stale value never re-triggers.
 */
export interface GatherMomentSource {
  get(): GatherMoment | null;
  subscribe(listener: () => void): () => void;
}

/** Everything App hands Game so it can drive the self-view gather tool. */
export interface GatherToolBridge {
  content: GatherToolContent;
  gather: GatherMomentSource;
}
